import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PrintContext } from './programModel';
import {
  CoverArrow, CoverFrame, CoverImage, PrintDecoAfter, PrintScatter, SponsorBlock, SponsorPage,
} from './programStyles';

const API = process.env.REACT_APP_API_URL || '';

// The admin's own pages of the program (PublicProgramView.Pages, uploaded in the admin's Programm
// tab): the title pages and the sponsor pages, as images the server serves (an uploaded image as it
// is, a PDF page rendered to PNG). The public page reads them from the event's public route; a page
// around the program that reads it elsewhere - the print page with a preview key, the admin's preview
// with its login - provides its own (pageId, index) => url here. A resolver may answer nothing for an
// image it has not loaded yet: that page is left out until it has.
export const PageUrlContext = createContext(null);

const usePageUrl = (program) => {
  const custom = useContext(PageUrlContext);
  if (custom) return custom;
  const slug = encodeURIComponent(program?.event?.slug || '');
  return (pageId, index) => `${API}/api/public/events/${slug}/program/pages/${pageId}/${index}`;
};

const pageKey = (p) => `${p.pageId}-${p.index}`;

// The sponsor pages the website shows, or with `print` those of the PDF (each file can be kept off
// either: PublicProgramView.PageImage's website and pdf).
export const shownSponsors = (program, print) => (program?.pages?.sponsors || [])
  .filter((p) => (print ? p.pdf : p.website) !== false);

// The sponsor pages, where the admin placed them in the program (ProgramSectionView, ProgramDay).
// On the website one below the other at the program's width; printed, every one on a page of its own
// (data-print-break-pages: ProgramPrintCopy measures the block). breakAfter: printed content follows,
// which then starts on a new page - and with it flowersAfter, the flowers of the pages after the
// sponsor pages, in a layer that starts there (ProgramSectionView's printScatter).
export const SponsorPages = ({ program, breakAfter, flowersAfter = [] }) => {
  const intl = useIntl();
  const print = useContext(PrintContext);
  const url = usePageUrl(program);
  const pages = shownSponsors(program, print);
  if (pages.length === 0) return null;
  const label = intl.formatMessage({ id: 'program.public.sponsors', defaultMessage: 'Sponsors' });
  return (
    <>
      <SponsorBlock aria-label={label} data-print-break-pages={pages.length} $breakAfter={breakAfter}>
        {pages.map((p, i) => {
          const src = url(p.pageId, p.index);
          return (
            <SponsorPage key={pageKey(p)}>
              {src && (
                <img src={src} alt={pages.length > 1 ? `${label} ${i + 1}/${pages.length}` : label} loading={print ? 'eager' : 'lazy'} />
              )}
            </SponsorPage>
          );
        })}
      </SponsorBlock>
      {print && flowersAfter.length > 0 && (
        <PrintDecoAfter aria-hidden="true">
          {flowersAfter.map((f, i) => (
            // eslint-disable-next-line react/no-array-index-key -- a fixed list
            <PrintScatter key={i} src={f.src} alt="" $side={f.side} $top={f.top} $width={f.width} $offset={f.offset}
              $opacity={f.opacity} $mirror={f.mirror} $rotate={f.rotate} />
          ))}
        </PrintDecoAfter>
      )}
    </>
  );
};

// How long a title page stands before the next one fades in.
export const COVER_SLIDE_MS = 6000;

const reducedMotion = () => typeof window !== 'undefined'
  && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// The title pages where the event page shows its main image (EventDetail, and the admin's preview):
// one alone as it is; several as a slideshow that fades from one to the next every COVER_SLIDE_MS,
// with an arrow at each edge to go back and forth (shown while the pointer is over it or a key is in
// it). It stands still while the pointer or a key is in it, and for people who ask for reduced motion.
export const CoverPages = ({ program, alt }) => {
  const intl = useIntl();
  const url = usePageUrl(program);
  const pages = (program?.pages?.cover || []).filter((p) => p.website !== false);
  const [shown, setShown] = useState(0);
  const [held, setHeld] = useState(false);
  const pressed = useRef(false); // the focus that follows comes from a click or a tap, not a key
  const count = pages.length;

  // every page stands its full time, also one an arrow has just brought
  useEffect(() => {
    if (count < 2 || held || reducedMotion()) return undefined;
    const timer = setTimeout(() => setShown((i) => (i + 1) % count), COVER_SLIDE_MS);
    return () => clearTimeout(timer);
  }, [count, held, shown]);

  if (count === 0) return null;
  const at = Math.min(shown, count - 1);
  const pageText = (i) => intl.formatMessage(
    { id: 'program.public.coverPage', defaultMessage: 'Title page {n} of {count}' },
    { n: i + 1, count },
  );
  const step = (by) => setShown((at + by + count) % count);
  return (
    <CoverFrame
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      // a key in it holds it; the focus a click on an arrow leaves behind does not
      onPointerDown={() => { pressed.current = true; }}
      onFocus={() => { if (!pressed.current) setHeld(true); pressed.current = false; }}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setHeld(false); }}
      aria-roledescription={count > 1 ? 'carousel' : undefined}
    >
      {pages.map((p, i) => {
        const src = url(p.pageId, p.index);
        return src && (
          <CoverImage
            key={pageKey(p)}
            src={src}
            alt={i === at ? (count > 1 ? `${alt} – ${pageText(i)}` : alt) : ''}
            aria-hidden={i === at ? undefined : true}
            $shown={i === at}
          />
        );
      })}
      {count > 1 && (
        <>
          <CoverArrow
            type="button"
            $side="left"
            onClick={() => step(-1)}
            aria-label={intl.formatMessage({ id: 'program.public.coverPrevious', defaultMessage: 'Previous title page' })}
          >
            <ChevronLeft size={24} aria-hidden="true" />
          </CoverArrow>
          <CoverArrow
            type="button"
            $side="right"
            onClick={() => step(1)}
            aria-label={intl.formatMessage({ id: 'program.public.coverNext', defaultMessage: 'Next title page' })}
          >
            <ChevronRight size={24} aria-hidden="true" />
          </CoverArrow>
        </>
      )}
    </CoverFrame>
  );
};
